import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { saveAs } from 'file-saver';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Subscription } from 'rxjs';
import { Company } from '../../../company/company.model';
import { TenantScope } from '../../../company/tenant-access.model';
import { TenantScopeService } from '../../../company/tenant-scope.service';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { DashboardCompanyScopeSelectorComponent } from '../../../shared/page-layout/dashboard-company-scope-selector.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import {
  HrAnalyticsReportPeriod,
  HrAnalyticsScorecardRule,
  HrAnnualManagementReport,
  HrAnnualMetricDetails,
  HrAnnualMetricMonth,
  HrAnnualMetricSeries,
  HrAnnualReportScope,
  HrCompanyAnnualReport
} from '../../hr.models';
import { HrService } from '../../hr.service';
import { HrActionDialogComponent } from '../../shared/hr-action-dialog.component';

interface SelectOption<T = string | number> {
  label: string;
  value: T;
}

interface MetricCategoryGroup {
  code: string;
  label: string;
  metrics: HrAnnualMetricSeries[];
}

@Component({
  selector: 'app-hr-annual-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    SelectModule,
    TagModule,
    TextareaModule,
    ToastModule,
    DashboardCompanyScopeSelectorComponent,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent,
    HrActionDialogComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-annual-report.component.html',
  styleUrls: ['./hr-annual-report.component.scss']
})
export class HrAnnualReportComponent implements OnInit, OnDestroy {
  private readonly hrService = inject(HrService);
  private readonly tenantScopeService = inject(TenantScopeService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);
  private readonly subscriptions = new Subscription();

  readonly canManage = this.permissionService.hasPermission(app_permissions.hr.analytics.manage);
  readonly canExport = this.permissionService.hasPermission(app_permissions.hr.analytics.export);
  readonly canLock = this.permissionService.hasPermission(app_permissions.hr.analytics.periodLock);
  readonly canOverride = this.permissionService.hasPermission(app_permissions.hr.analytics.override);
  readonly monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  readonly monthOptions: SelectOption<number>[] = this.monthNames.map((label, index) => ({ label, value: index + 1 }));
  readonly yearOptions: SelectOption<number>[] = Array.from({ length: 7 }, (_, index) => {
    const year = new Date().getFullYear() - 3 + index;
    return { label: String(year), value: year };
  });
  readonly statusOptions: SelectOption[] = ['GREEN', 'AMBER', 'RED', 'NOT_AVAILABLE']
    .map(value => ({ label: value.replace('_', ' '), value }));

  loading = false;
  report?: HrAnnualManagementReport;
  year = new Date().getFullYear();
  generationMonth = new Date().getMonth() + 1;
  private companyId: number | null = null;
  companyGroupId: number | null = null;
  activeGroupName = '';
  groupCompanies: Company[] = [];
  selectedGroupCompanyId: number | null = null;

  activeDialog?: 'generate' | 'details' | 'reopen' | 'scorecards' | 'override';
  selectedPeriod?: HrAnalyticsReportPeriod;
  selectedMetric?: HrAnnualMetricSeries;
  selectedMonth?: HrAnnualMetricMonth;
  selectedCompany?: HrCompanyAnnualReport;
  metricDetails?: HrAnnualMetricDetails;
  scorecards: HrAnalyticsScorecardRule[] = [];
  reopenReason = '';
  overrideStatus = 'AMBER';
  overrideReason = '';

  ngOnInit(): void {
    this.subscriptions.add(this.tenantScopeService.activeScope$.subscribe(scope => this.handleScope(scope)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get reportSections(): HrCompanyAnnualReport[] {
    return [
      ...(this.report?.consolidated ? [this.report.consolidated] : []),
      ...(this.report?.companies || [])
    ];
  }

  get selectedCompanyIdForSettings(): number | null {
    if (this.selectedGroupCompanyId) return this.selectedGroupCompanyId;
    return this.companyId;
  }

  get periodRows(): HrAnalyticsReportPeriod[] {
    return this.reportSections
      .filter(section => !section.consolidated)
      .flatMap(section => section.periods || [])
      .sort((left, right) => (left.owningCompanyId || 0) - (right.owningCompanyId || 0)
        || (left.reportMonth || 0) - (right.reportMonth || 0));
  }

  load(): void {
    const scope = this.apiScope();
    if (!scope.owningCompanyId && !scope.companyGroupId) return;
    this.loading = true;
    this.hrService.annualHrReport(scope, this.year).subscribe({
      next: response => {
        this.report = response.data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toast('error', 'Unable to load the annual HR management report.');
      }
    });
  }

  generate(): void {
    if (!this.canManage) return;
    this.hrService.generateAnalyticsReportPeriod(this.apiScope(), this.year, this.generationMonth).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', `${this.monthNames[this.generationMonth - 1]} reporting snapshots generated.`);
        this.load();
      },
      error: error => this.toast('error', error?.error?.message || 'Unable to generate the reporting period.')
    });
  }

  review(period: HrAnalyticsReportPeriod): void {
    this.transition(period, 'review');
  }

  lock(period: HrAnalyticsReportPeriod): void {
    this.transition(period, 'lock');
  }

  openReopen(period: HrAnalyticsReportPeriod): void {
    this.selectedPeriod = period;
    this.reopenReason = '';
    this.activeDialog = 'reopen';
  }

  reopen(): void {
    if (!this.selectedPeriod?.analyticsReportPeriodId || !this.reopenReason.trim()) return;
    this.transition(this.selectedPeriod, 'reopen', this.reopenReason);
  }

  openMetric(company: HrCompanyAnnualReport, metric: HrAnnualMetricSeries, month: HrAnnualMetricMonth): void {
    if (company.consolidated || !company.owningCompanyId || month.periodStatus === 'NOT_GENERATED') return;
    this.selectedCompany = company;
    this.selectedMetric = metric;
    this.selectedMonth = month;
    this.metricDetails = undefined;
    this.activeDialog = 'details';
    this.hrService.annualHrMetricDetails(
      this.apiScope(company.owningCompanyId),
      company.owningCompanyId,
      this.year,
      month.month,
      metric.metricCode
    ).subscribe({
      next: response => this.metricDetails = response.data,
      error: () => this.toast('error', 'Unable to load metric details.')
    });
  }

  openOverride(): void {
    if (!this.canOverride || !this.selectedMonth) return;
    this.overrideStatus = this.selectedMonth.scorecardStatus || 'AMBER';
    this.overrideReason = '';
    this.activeDialog = 'override';
  }

  saveOverride(): void {
    const period = this.selectedCompany?.periods?.find(row => row.reportMonth === this.selectedMonth?.month);
    if (!period?.analyticsReportPeriodId || !this.selectedMetric || !this.selectedCompany?.owningCompanyId
      || !this.overrideReason.trim()) return;
    this.hrService.overrideAnnualHrStatus(
      period.analyticsReportPeriodId,
      this.selectedMetric.metricCode,
      this.apiScope(this.selectedCompany.owningCompanyId),
      this.overrideStatus,
      this.overrideReason
    ).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', 'Audited scorecard override saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save the scorecard override.')
    });
  }

  openScorecards(): void {
    const companyId = this.selectedCompanyIdForSettings;
    if (!companyId) {
      this.toast('error', 'Select a company before editing scorecards.');
      return;
    }
    this.hrService.analyticsScorecards(companyId).subscribe({
      next: response => {
        this.scorecards = response.data || [];
        this.activeDialog = 'scorecards';
      },
      error: () => this.toast('error', 'Unable to load scorecard rules.')
    });
  }

  saveScorecard(rule: HrAnalyticsScorecardRule): void {
    const companyId = this.selectedCompanyIdForSettings;
    if (!companyId || !this.canManage) return;
    this.hrService.saveAnalyticsScorecard(companyId, rule).subscribe({
      next: response => {
        Object.assign(rule, response.data);
        this.toast('success', `${rule.metricCode} scorecard saved.`);
      },
      error: () => this.toast('error', 'Unable to save the scorecard.')
    });
  }

  exportWorkbook(): void {
    if (!this.canExport) return;
    this.hrService.annualHrReportWorkbook(this.apiScope(), this.year).subscribe({
      next: response => saveAs(response.body!, `annual-hr-management-report-${this.year}.xlsx`),
      error: () => this.toast('error', 'Unable to export the annual HR workbook.')
    });
  }

  selectGroupCompany(companyId: number | null): void {
    this.selectedGroupCompanyId = companyId;
    this.load();
  }

  groupedMetrics(company: HrCompanyAnnualReport): MetricCategoryGroup[] {
    const groups = new Map<string, HrAnnualMetricSeries[]>();
    for (const metric of company.metrics || []) {
      const values = groups.get(metric.metricCategory) || [];
      values.push(metric);
      groups.set(metric.metricCategory, values);
    }
    return Array.from(groups.entries()).map(([code, metrics]) => ({
      code,
      label: code.replaceAll('_', ' '),
      metrics
    }));
  }

  companyName(companyId?: number): string {
    return this.report?.companies?.find(company => company.owningCompanyId === companyId)?.companyName
      || `Company ${companyId}`;
  }

  statusSeverity(status?: string): 'success' | 'warn' | 'danger' | 'secondary' | 'info' {
    switch (status) {
      case 'GREEN':
      case 'LOCKED':
        return 'success';
      case 'AMBER':
      case 'REVIEWED':
      case 'PARTIAL':
        return 'warn';
      case 'RED':
      case 'MISSING':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  sparkline(metric: HrAnnualMetricSeries): string {
    const values = metric.months.map(month => month.value).filter((value): value is number => value !== undefined && value !== null);
    if (!values.length) return '';
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    return metric.months.map((month, index) => {
      const value = month.value ?? min;
      const x = index * 10;
      const y = 26 - ((value - min) / range) * 22;
      return `${x},${y}`;
    }).join(' ');
  }

  private transition(period: HrAnalyticsReportPeriod, action: 'review' | 'lock' | 'reopen', reason?: string): void {
    if (!period.analyticsReportPeriodId || !period.owningCompanyId || !this.canLock) return;
    this.hrService.transitionAnalyticsReportPeriod(
      period.analyticsReportPeriodId,
      action,
      this.apiScope(period.owningCompanyId),
      reason
    ).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', `Reporting period ${action === 'review' ? 'reviewed' : action === 'lock' ? 'locked' : 'reopened'}.`);
        this.load();
      },
      error: error => this.toast('error', error?.error?.message || `Unable to ${action} the reporting period.`)
    });
  }

  private handleScope(scope: TenantScope | null): void {
    if (scope?.type === 'GROUP') {
      this.companyId = null;
      this.companyGroupId = scope.companyGroupId;
      this.activeGroupName = scope.companyGroupName;
      this.groupCompanies = scope.memberCompanies || [];
      this.selectedGroupCompanyId = null;
    } else if (scope?.type === 'COMPANY') {
      this.companyId = scope.companyId;
      this.companyGroupId = null;
      this.activeGroupName = '';
      this.groupCompanies = [];
      this.selectedGroupCompanyId = null;
    } else {
      this.companyId = null;
      this.companyGroupId = null;
    }
    this.load();
  }

  private apiScope(forceCompanyId?: number): HrAnnualReportScope {
    if (this.companyGroupId) {
      return {
        companyGroupId: this.companyGroupId,
        memberCompanyId: forceCompanyId || this.selectedGroupCompanyId || undefined,
        scopeName: this.activeGroupName
      };
    }
    return {
      owningCompanyId: forceCompanyId || this.companyId || undefined,
      scopeName: this.report?.scopeName
    };
  }

  private toast(severity: 'success' | 'error', detail: string): void {
    this.messageService.add({
      severity,
      summary: severity === 'success' ? 'Annual HR Report' : 'Unable to complete',
      detail
    });
  }
}
